'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="How Much Plastic Do We Eat Per Week? The Credit-Card Stat Explained"
      author="MicroPlastics Editorial Team"
      date="April 20, 2026"
      readTime="8 min read"
      heroImage="/blog/app-home.png"
      heroImageAlt="How much plastic the average person eats per week"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        The viral claim that humans &ldquo;eat a credit card of plastic per week&rdquo;
        (~5 grams) comes from a 2019 University of Newcastle / WWF report. Subsequent
        peer-reviewed analyses have <strong>narrowed the figure significantly</strong>:
        a 2021 review estimated weekly ingestion at 0.1–5 grams depending on diet,
        and a 2024 review suggested the credit-card figure may overstate it. The
        most defensible current estimate is around <strong>0.4 to 4 grams per week</strong>,
        with bottled-water drinkers at the upper end and filtered-water users at the
        lower end.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>The &ldquo;credit-card per week&rdquo; figure is real-but-contested: WWF&apos;s 2019 report cited Cox et al. 2019 in <em>Environmental Science &amp; Technology</em>.</>,
          <>Bottled-water drinkers can ingest 22× more microplastic than tap-water drinkers, making bottled water the single biggest exposure variable.</>,
          <>Updated 2021–2024 reviews estimate weekly ingestion of <strong>0.1 to 5 grams</strong>, with most defensible mid-range estimates around 0.4–4 grams.</>,
          <>Annual exposure adds up: even at 0.4 g/week, that&apos;s about 21 grams per year, or roughly the weight of a plastic hanger.</>,
          <>Cutting bottled water alone can drop your weekly intake by 60–80% in most diets.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Where the credit-card figure came from
      </h2>
      <p>
        In June 2019, WWF commissioned a literature review from the University of
        Newcastle (Australia) titled <em>No Plastic in Nature: Assessing Plastic
        Ingestion from Nature to People</em>. The review estimated that an average
        person ingests roughly <strong>2,000 microplastic particles per week</strong> (about 5 grams) equivalent in mass to a single credit card.
      </p>
      <p>
        The figure went viral because it was concrete, visual, and shareable. It
        was also based on extrapolation, not direct measurement of individuals.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How the figure was calculated
      </h2>
      <p>
        The Cox et al. 2019 paper that underpinned WWF&apos;s claim estimated
        ingestion across major dietary sources:
      </p>
      <BlogComparisonTable
        caption="Estimated microplastic ingestion by source (Cox et al. 2019)"
        columns={[
          { key: 'source', label: 'Source' },
          { key: 'annual', label: 'Particles per year' },
          { key: 'weekly', label: 'Particles per week' },
        ]}
        rows={[
          { source: 'Water (bottled)', annual: '~90,000', weekly: '~1,730' },
          { source: 'Water (tap only)', annual: '~4,000', weekly: '~75' },
          { source: 'Air (inhalation)', annual: '~38,000–90,000', weekly: '~730–1,730' },
          { source: 'Salt', annual: '~11,000', weekly: '~210' },
          { source: 'Shellfish', annual: '~11,000', weekly: '~210' },
          { source: 'Sugar', annual: '~3,000', weekly: '~58' },
          { source: 'Total (typical Western diet, bottled water)', annual: '~158,000', weekly: '~3,000' },
        ]}
      />
      <p>
        Converting <em>particles</em> to <em>mass</em> requires assumptions about
        average particle size and density. The 5-gram credit-card mass came from
        applying typical PET density to these particle counts, a step several
        researchers have since challenged as overestimating average particle size.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The 2021 and 2024 reanalyses
      </h2>
      <p>
        A 2021 review by <strong>Senathirajah et al. in <em>Journal of Hazardous Materials</em></strong>
        applied stricter weighting and concluded the typical human ingests
        approximately <strong>0.1–5 grams of microplastics per week</strong>, with
        0.7 g/week being a defensible mid-point. They also pointed out that the
        bottled-water signal dominated the original WWF figure.
      </p>
      <p>
        A 2024 review by Mohamed Nor et al. in <em>Environmental Science &amp; Technology</em> (which incorporated the 2024 PNAS nanoplastic data) argued that the credit-card
        figure <em>understates</em> if you count nanoplastics by particle but
        <em> overstates</em> if you count by mass, because nanoplastics weigh
        essentially nothing individually.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What the figure means depending on your habits
      </h2>
      <BlogComparisonTable
        caption="Estimated weekly microplastic intake by lifestyle"
        columns={[
          { key: 'lifestyle', label: 'Lifestyle profile' },
          { key: 'mass', label: 'Estimated weekly mass' },
          { key: 'visual', label: 'Visual equivalent' },
        ]}
        rows={[
          { lifestyle: 'Bottled water + microwave-plastic + takeout heavy', mass: '~4–5 g/week', visual: 'About 1 credit card' },
          { lifestyle: 'Average Western diet, mixed habits', mass: '~0.5–2 g/week', visual: 'About 1 grain of rice' },
          { lifestyle: 'Filtered water, glass storage, low-takeout', mass: '~0.1–0.5 g/week', visual: 'About 1 pencil shaving' },
          { lifestyle: 'Same as above + HEPA air + chicle gum + no plastic-heated food', mass: '~0.05–0.2 g/week', visual: 'Trace' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Does eating 0.5 to 5 grams of plastic per week actually harm you?
      </h2>
      <p>
        That question isn&apos;t yet answered with certainty, but the trend is moving
        toward &ldquo;yes&rdquo;. Animal studies consistently show inflammation,
        oxidative stress, and gut microbiome disruption at exposures comparable to
        human dietary intake. The first human prospective study (NEJM 2024) found
        a 4.53× higher cardiovascular-event risk in patients with microplastics in
        their arterial plaque. See our{' '}
        <Link href="/blog/microplastics-in-arterial-plaque" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          arterial plaque study explainer
        </Link>{' '}
        for details.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The five highest-impact reductions
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li>Switch from bottled water to filtered tap (removes the single biggest source)</li>
        <li>Stop heating food in plastic (removes the biggest amplifier per gram)</li>
        <li>Replace plastic food storage with glass</li>
        <li>HEPA-filter indoor air, especially bedrooms</li>
        <li>Reduce takeout and canned foods (epoxy can liners + plastic-lined paper)</li>
      </ol>
      <p>
        See{' '}
        <Link href="/how-to-avoid-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          how to avoid microplastics
        </Link>{' '}
        for the full 50-item checklist.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Product packaging. PET, HDPE, PP, PS, PVC, multi-layer, glass, aluminum.</>,
          <>Container condition from photo, scratches, dents, fade.</>,
          <>Brand and product category, flags for known PFAS / BPA / fragranced lines.</>,
          <>Use-context flags, heat exposure, microwave, reuse cycles.</>,
          <>Cited research, every score links the specific studies behind it.</>,
        ]}
      />

      <BlogAppCTA
        headline="Translate the research into 5-second shelf decisions"
        sub="Reading the studies is step one. Acting on them at the grocery store is step two. The MicroPlastics app scores each product 0–100 using research like this."
        buttonLabel="Get the MicroPlastics app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Do we really eat a credit card of plastic per week?',
            answer:
              'The "5 grams per week" figure comes from a 2019 University of Newcastle / WWF report. Subsequent peer-reviewed reviews place typical weekly intake between 0.1 and 5 grams, with most estimates around 0.4 to 2 grams depending on diet.',
          },
          {
            question: 'How much microplastic is in the average person per year?',
            answer:
              'Combining major exposure sources, the average Western diet results in roughly 21 to 100+ grams of microplastic ingested per year. Bottled-water drinkers are at the upper end; filtered-water and glass-storage users are at the lower end.',
          },
          {
            question: 'What is the single biggest source of microplastics in our diet?',
            answer:
              'Bottled water. A 2024 Columbia/Rutgers study found bottled water averages 240,000 nanoplastic particles per liter. 10 to 100 times higher than tap water. Cutting bottled water alone reduces weekly intake by an estimated 60-80% in most diets.',
          },
          {
            question: 'How can I measure how much plastic I eat?',
            answer:
              'There is no consumer test for microplastic intake yet. The best proxy is tracking your major exposure sources: bottled vs filtered water, plastic vs glass food storage, frequency of microwaving plastic, and takeout consumption. The MicroPlastics app helps estimate exposure from scanned products.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Cox KD, Covernton GA, Davies HL, et al.',
            year: 2019,
            title: 'Human consumption of microplastics',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.9b01517',
          },
          {
            authors: 'Senathirajah K, Attwood S, Bhagwat G, et al.',
            year: 2021,
            title: 'Estimation of the mass of microplastics ingested - A pivotal first step towards human health risk assessment',
            publisher: 'Journal of Hazardous Materials',
            url: 'https://www.sciencedirect.com/science/article/pii/S0304389420321257',
          },
          {
            authors: 'Mohamed Nor NH, Kooi M, Diepens NJ, Koelmans AA',
            year: 2021,
            title: 'Lifetime accumulation of microplastic in children and adults',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.0c07384',
          },
          {
            authors: 'Qian N, Gao X, Lang X, et al.',
            year: 2024,
            title: 'Rapid single-particle chemical imaging of nanoplastics by SRS microscopy',
            publisher: 'PNAS',
            url: 'https://www.pnas.org/doi/10.1073/pnas.2300582121',
          },
          {
            authors: 'WWF International',
            year: 2019,
            title: 'No Plastic in Nature: Assessing Plastic Ingestion from Nature to People',
            publisher: 'WWF',
            url: 'https://wwfint.awsassets.panda.org/downloads/plastic_ingestion_press_singles.pdf',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
